// resources/js/src/components/community/components/profile/ReportUserModal.tsx
import { Fragment, useState } from "react";
import { Dialog, Transition, RadioGroup } from "@headlessui/react";
import { Icon } from "@iconify/react";
import { reportApi, ReportReason } from "../../../../../services/report.api";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
};

const REASONS: {
  value: ReportReason;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    value: "spam",
    label: "Spam",
    description: "Unwanted commercial content or repetitive posts.",
    icon: "mdi:email-alert-outline",
  },
  {
    value: "harassment",
    label: "Harassment",
    description: "Bullying, threats, or targeted attacks.",
    icon: "mdi:account-alert-outline",
  },
  {
    value: "fake_account",
    label: "Fake Account",
    description: "Impersonation or a profile pretending to be someone else.",
    icon: "mdi:account-question-outline",
  },
  {
    value: "inappropriate_content",
    label: "Inappropriate Content",
    description: "Nudity, graphic content, or other inappropriate media.",
    icon: "mdi:eye-off-outline",
  },
  {
    value: "hate_speech",
    label: "Hate Speech",
    description: "Content that promotes hatred against a group.",
    icon: "mdi:message-alert-outline",
  },
  {
    value: "violence",
    label: "Violence",
    description: "Threats of harm or violent content.",
    icon: "mdi:alert-octagon-outline",
  },
  {
    value: "other",
    label: "Other",
    description: "Something else that violates our guidelines.",
    icon: "mdi:dots-horizontal-circle-outline",
  },
];

export default function ReportUserModal({
  isOpen,
  onClose,
  userId,
  userName,
}: Props) {
  const [reason, setReason] = useState<ReportReason>("spam");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    if (loading) return;
    onClose();
    // reset بعد الإغلاق
    setTimeout(() => {
      setSuccess(false);
      setDetails("");
      setError("");
      setReason("spam");
    }, 300);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      await reportApi.submit({ userId, reason, details });
      setSuccess(true);
      setTimeout(() => handleClose(), 1800);
    } catch (e: any) {
      setError(e?.message ?? "Failed to submit report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* ===== Backdrop ===== */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* ===== Dialog Container ===== */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="relative w-full max-w-lg rounded-2xl bg-white dark:bg-deepSlate shadow-2xl overflow-hidden">
              {/* ===== Header ===== */}
              <div className="relative px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center shrink-0">
                    <Icon
                      icon="mdi:flag-outline"
                      className="text-red-500 text-2xl"
                    />
                  </div>
                  <div className="flex-1">
                    <Dialog.Title className="text-lg font-bold text-gray-900 dark:text-white">
                      Report {userName}
                    </Dialog.Title>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Help us keep the community safe. Your report is anonymous.
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    aria-label="Close"
                  >
                    <Icon icon="mdi:close" className="text-xl" />
                  </button>
                </div>
              </div>

              {/* ===== Body ===== */}
              {success ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                    <Icon
                      icon="mdi:check-circle-outline"
                      className="text-green-500 text-4xl"
                    />
                  </div>
                  <p className="text-green-600 dark:text-green-400 font-semibold text-lg">
                    Report submitted
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Our team will review it shortly.
                  </p>
                </div>
              ) : (
                <>
                  <div className="p-6 max-h-[50vh] overflow-y-auto">
                    {/* ===== Reasons ===== */}
                    <RadioGroup value={reason} onChange={setReason}>
                      <RadioGroup.Label className="sr-only">
                        Select a reason
                      </RadioGroup.Label>
                      <div className="space-y-2">
                        {REASONS.map((r) => (
                          <RadioGroup.Option
                            key={r.value}
                            value={r.value}
                            className={({ checked }) =>
                              `relative flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 border ${
                                checked
                                  ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-500"
                                  : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                              }`
                            }
                          >
                            {({ checked }) => (
                              <>
                                <Icon
                                  icon={r.icon}
                                  className={`text-xl mt-0.5 shrink-0 ${
                                    checked
                                      ? "text-primary-600 dark:text-primary-400"
                                      : "text-gray-400"
                                  }`}
                                />
                                <div className="flex-1 min-w-0">
                                  <RadioGroup.Label
                                    className={`block text-sm font-semibold ${
                                      checked
                                        ? "text-primary-700 dark:text-primary-300"
                                        : "text-gray-800 dark:text-gray-200"
                                    }`}
                                  >
                                    {r.label}
                                  </RadioGroup.Label>
                                  <RadioGroup.Description
                                    className={`block text-xs mt-0.5 ${
                                      checked
                                        ? "text-primary-600/80 dark:text-primary-400/80"
                                        : "text-gray-500 dark:text-gray-400"
                                    }`}
                                  >
                                    {r.description}
                                  </RadioGroup.Description>
                                </div>
                                {checked && (
                                  <Icon
                                    icon="mdi:check-circle"
                                    className="text-primary-600 dark:text-primary-400 text-xl shrink-0"
                                  />
                                )}
                              </>
                            )}
                          </RadioGroup.Option>
                        ))}
                      </div>
                    </RadioGroup>

                    {/* ===== Details ===== */}
                    <div className="mt-5">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Additional details{" "}
                        <span className="text-gray-400 font-normal">
                          (optional)
                        </span>
                      </label>
                      <textarea
                        value={details}
                        onChange={(e) => setDetails(e.target.value)}
                        placeholder="Provide any additional context that can help us review this report..."
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        rows={3}
                        maxLength={500}
                      />
                      <p className="text-xs text-gray-400 mt-1 text-right">
                        {details.length}/500
                      </p>
                    </div>

                    {/* ===== Error ===== */}
                    {error && (
                      <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
                        <Icon
                          icon="mdi:alert-circle-outline"
                          className="text-red-500 text-lg shrink-0"
                        />
                        <p className="text-xs text-red-600 dark:text-red-400">
                          {error}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* ===== Footer ===== */}
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2">
                    <button
                      onClick={handleClose}
                      disabled={loading}
                      className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-all duration-200 active:scale-[0.97]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg shadow-red-600/25 hover:shadow-xl hover:shadow-red-600/40 hover:from-red-700 hover:to-red-600 disabled:opacity-50 transition-all duration-200 active:scale-[0.97]"
                    >
                      {loading ? (
                        <>
                          <Icon
                            icon="mdi:loading"
                            className="text-lg animate-spin"
                          />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:send-outline" className="text-lg" />
                          Submit Report
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}